import { useState, useEffect, useCallback } from 'react';

/**
 * Hook avançado para detecção de dispositivos móveis
 * Detecta tipo de dispositivo, orientação, e recursos de toque
 */
export const useMobileDetect = () => {
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    isIOS: false,
    isAndroid: false,
    isSafari: false,
    isChrome: false,
    isFirefox: false,
    isPWA: false,
    hasTouch: false,
    orientation: 'portrait',
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
    screenHeight: typeof window !== 'undefined' ? window.innerHeight : 1080,
    pixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    connection: null
  });

  const detectDevice = useCallback(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return;
    }

    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Device type detection
    const isMobile = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(userAgent) || 
      (navigator.maxTouchPoints > 0 && window.innerWidth >= 768 && window.innerWidth <= 1024);
    const isDesktop = !isMobile && !isTablet;

    // OS detection
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isAndroid = /Android/i.test(userAgent);

    // Browser detection
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
    const isChrome = /Chrome/i.test(userAgent) && !/Edge/i.test(userAgent);
    const isFirefox = /Firefox/i.test(userAgent);

    // PWA detection
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    // Touch capability
    const hasTouch = 'ontouchstart' in window || 
      navigator.maxTouchPoints > 0 || 
      navigator.msMaxTouchPoints > 0;

    // Orientation
    const orientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';

    // Connection info
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    setDeviceInfo({
      isMobile,
      isTablet,
      isDesktop,
      isIOS,
      isAndroid,
      isSafari,
      isChrome,
      isFirefox,
      isPWA,
      hasTouch,
      orientation,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      pixelRatio: window.devicePixelRatio,
      isOnline: navigator.onLine,
      connection: connection ? {
        type: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      } : null
    });
  }, []);

  useEffect(() => {
    detectDevice();

    // Listen for resize events
    const handleResize = () => {
      detectDevice();
    };

    // Listen for orientation changes
    const handleOrientationChange = () => {
      // Delay to allow browser to update dimensions
      setTimeout(detectDevice, 100);
    };

    // Listen for online/offline events
    const handleOnline = () => setDeviceInfo(prev => ({ ...prev, isOnline: true }));
    const handleOffline = () => setDeviceInfo(prev => ({ ...prev, isOnline: false }));

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [detectDevice]);

  return deviceInfo;
};

/**
 * Hook para ajustar viewport height em dispositivos móveis
 * Corrige o problema do 100vh em navegadores mobile
 */
export const useMobileViewportHeight = () => {
  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', () => {
      setTimeout(setViewportHeight, 100);
    });

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
    };
  }, []);
};

/**
 * Hook para detectar e prevenir zoom em inputs no iOS
 */
export const usePreventIOSZoom = () => {
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (!isIOS) return;

    // Add viewport meta to prevent zoom
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      const content = viewportMeta.getAttribute('content');
      if (!content.includes('maximum-scale')) {
        viewportMeta.setAttribute('content', content + ', maximum-scale=1.0');
      }
    }

    // Prevent zoom on input focus
    const handleFocus = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        const fontSize = window.getComputedStyle(e.target).fontSize;
        const fontSizeNum = parseFloat(fontSize);
        if (fontSizeNum < 16) {
          e.target.style.fontSize = '16px';
        }
      }
    };

    document.addEventListener('focus', handleFocus, true);

    return () => {
      document.removeEventListener('focus', handleFocus, true);
    };
  }, []);
};

export default useMobileDetect;
